/**
 * SubscriberSignupChart
 *
 * Per-bucket signup trend chart shown above the Subscribers list. A single
 * component is reused across the All / Email-list / Paid-subscribers tabs;
 * the dataset it emphasizes is driven by the `type` prop. A segmented
 * control toggles the time range (Week / Month / Year / All time).
 *
 * Data comes from `useSubscriberSignupTimeseries`, which already fills
 * zero-count buckets on the server so the line renders continuously
 * without client-side gap filling.
 */
import { Loader2, TrendingUp } from "lucide-react";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useSubscriberSignupTimeseries,
  type SignupTimeseriesRange,
  type SubscriberKind,
} from "@/hooks/useSubscribersAdmin";

interface Props {
  /** Drives which series the chart emphasizes. "all" shows both. */
  type: SubscriberKind | "all";
  range: SignupTimeseriesRange;
  onRangeChange: (range: SignupTimeseriesRange) => void;
}

const RANGE_OPTIONS: Array<{ value: SignupTimeseriesRange; label: string }> = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
  { value: "all", label: "All time" },
];

const COLORS = {
  email: "#B87333", // Brand copper — email list
  paid: "#3B82F6", // Brand blue — paid subscribers
};

function formatBucketLabel(
  date: string,
  bucket: "day" | "week" | "month",
): string {
  // `date` is YYYY-MM-DD for day/week buckets and YYYY-MM-01 for month.
  // Parse as a local date (avoid the UTC shift "1999-01-01" → Dec 31 in
  // negative-offset timezones) by splitting components manually.
  const [yStr, mStr, dStr] = date.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  if (!y || !m || !d) return date;
  const local = new Date(y, m - 1, d);
  if (bucket === "month") {
    return local.toLocaleDateString(undefined, {
      month: "short",
      year: "2-digit",
    });
  }
  if (bucket === "week") {
    return local.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
  return local.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function SubscriberSignupChart({ type, range, onRangeChange }: Props) {
  const { data, isLoading, error } = useSubscriberSignupTimeseries(range);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.series.map((p) => ({
      ...p,
      label: formatBucketLabel(p.date, data.bucket),
    }));
  }, [data]);

  // Totals for the header — reflect the currently-selected tab.
  const totals = useMemo(() => {
    if (!data) return { email: 0, paid: 0 };
    return data.series.reduce(
      (acc, p) => {
        acc.email += p.email;
        acc.paid += p.paid;
        return acc;
      },
      { email: 0, paid: 0 },
    );
  }, [data]);

  const showEmail = type !== "paid";
  const showPaid = type !== "email";

  const rangeLabel =
    range === "week"
      ? "this week"
      : range === "month"
        ? "last 30 days"
        : range === "year"
          ? "last 12 months"
          : "all time";

  const totalCopy = (() => {
    if (type === "email") {
      return `${totals.email.toLocaleString()} new email signups · ${rangeLabel}`;
    }
    if (type === "paid") {
      return `${totals.paid.toLocaleString()} new paid subscribers · ${rangeLabel}`;
    }
    return `${totals.email.toLocaleString()} email · ${totals.paid.toLocaleString()} paid · ${rangeLabel}`;
  })();

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#B87333]" />
            Signups over time
          </CardTitle>
          <CardDescription>{totalCopy}</CardDescription>
        </div>
        <div
          role="group"
          aria-label="Chart time range"
          className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 shadow-sm self-start"
        >
          {RANGE_OPTIONS.map((opt) => {
            const active = range === opt.value;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="ghost"
                size="sm"
                aria-pressed={active}
                onClick={() => onRangeChange(opt.value)}
                className={
                  active
                    ? "bg-[#B87333] text-white hover:bg-[#A67329] hover:text-white px-3 h-8"
                    : "text-slate-700 hover:bg-slate-100 px-3 h-8"
                }
              >
                {opt.label}
              </Button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && !data ? (
          <div className="flex h-56 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#B87333]" />
          </div>
        ) : error ? (
          <div className="flex h-56 items-center justify-center text-sm text-slate-600">
            Unable to load signup trend.
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-56 items-center justify-center text-sm text-slate-500">
            No signups recorded yet in this range.
          </div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
              >
                <defs>
                  <linearGradient
                    id="signup-email-fill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={COLORS.email}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="100%"
                      stopColor={COLORS.email}
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                  <linearGradient
                    id="signup-paid-fill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={COLORS.paid}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="100%"
                      stopColor={COLORS.paid}
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  minTickGap={16}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  width={32}
                />
                <Tooltip
                  formatter={(value, name) => [
                    Number(value).toLocaleString(),
                    String(name),
                  ]}
                  labelFormatter={(label) => String(label)}
                />
                {showEmail && showPaid && <Legend />}
                {showEmail && (
                  <Area
                    type="monotone"
                    dataKey="email"
                    name="Email list"
                    stroke={COLORS.email}
                    strokeWidth={2}
                    fill="url(#signup-email-fill)"
                  />
                )}
                {showPaid && (
                  <Area
                    type="monotone"
                    dataKey="paid"
                    name="Paid subscribers"
                    stroke={COLORS.paid}
                    strokeWidth={2}
                    fill="url(#signup-paid-fill)"
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SubscriberSignupChart;
